import { useEffect, useState, useCallback } from "react";
import { http, fmtErr } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import FoodTaxSettings from "@/pages/admin/dashboard/settings/FoodTaxSettings";

export default function MenuManager() {
  const [cats, setCats] = useState([]);

  const load = useCallback(() => http.get("/admin/food-items").then((r) => setCats(r.data.categories)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const saveItem = async (it, patch) => {
    try {
      await http.put(`/admin/food-items/${it.id}`, {
        category_id: it.category_id, name: it.name, description: it.description || "",
        price: it.price, tax_rate: it.tax_rate ?? 5, veg: it.veg ?? true,
        spicy_level: it.spicy_level ?? 0, preparation_time: it.preparation_time ?? 20,
        available: it.available ?? true, sort_order: it.sort_order ?? 999, image: it.image || "", ...patch,
      });
      toast.success("Saved");
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const delItem = async (id) => {
    try { await http.delete(`/admin/food-items/${id}`); toast.success("Item deleted"); load(); }
    catch (e) { toast.error(fmtErr(e)); }
  };

  return (
    <div className="space-y-6" data-testid="menu-manager">
      <FoodTaxSettings />
      <AddItemForm cats={cats} onAdded={load} />
      <CategoryManager cats={cats} onChanged={load} />
      {cats.map((c) => (
        <CategoryItems key={c.id} cat={c} onSave={saveItem} onDelete={delItem} />
      ))}
    </div>
  );
}

function AddItemForm({ cats, onAdded }) {
  const [form, setForm] = useState({ category_id: "", name: "", description: "", price: "", veg: true, tax_rate: 5 });

  const addItem = async () => {
    if (!form.category_id || !form.name || form.price === "") return toast.error("Category, name and price are required");
    try {
      await http.post("/admin/food-items", { ...form, price: parseFloat(form.price) });
      toast.success("Item added");
      setForm({ ...form, name: "", description: "", price: "" });
      onAdded();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  return (
    <div className="card">
      <h3 className="font-serif text-2xl mb-4">Add Food Item</h3>
      <div className="grid md:grid-cols-3 gap-3">
        <select data-testid="menu-form-category" value={form.category_id} onChange={(e) => setForm({ ...form, category_id: e.target.value })}>
          <option value="">Select category…</option>
          {cats.filter((c) => c.active).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <input data-testid="menu-form-name" placeholder="Item name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        <input data-testid="menu-form-price" type="number" min="0" placeholder="Price (₹)" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} />
        <input data-testid="menu-form-desc" placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="md:col-span-2" />
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" style={{ width: "auto" }} data-testid="menu-form-veg" checked={form.veg} onChange={(e) => setForm({ ...form, veg: e.target.checked })} /> Vegetarian</label>
        <button data-testid="menu-form-add" onClick={addItem} className="btn-primary flex items-center justify-center gap-2"><Plus size={16} /> Add Item</button>
      </div>
    </div>
  );
}

function CategoryManager({ cats, onChanged }) {
  const [newCat, setNewCat] = useState("");

  const addCat = async () => {
    if (!newCat.trim()) return;
    try { await http.post("/admin/food-categories", { name: newCat.trim() }); setNewCat(""); onChanged(); toast.success("Category added"); }
    catch (e) { toast.error(fmtErr(e)); }
  };

  const renameCat = async (c) => {
    const name = window.prompt("Rename category", c.name);
    if (!name || name === c.name) return;
    try { await http.put(`/admin/food-categories/${c.id}`, { name, sort_order: c.sort_order, active: c.active }); onChanged(); }
    catch (e) { toast.error(fmtErr(e)); }
  };

  const toggleCat = async (c) => {
    try { await http.put(`/admin/food-categories/${c.id}`, { name: c.name, sort_order: c.sort_order, active: !c.active }); onChanged(); }
    catch (e) { toast.error(fmtErr(e)); }
  };

  return (
    <div className="card">
      <h3 className="font-serif text-2xl mb-4">Categories</h3>
      <div className="flex gap-2 mb-4">
        <input data-testid="menu-new-category" placeholder="New category name" value={newCat} onChange={(e) => setNewCat(e.target.value)} />
        <button data-testid="menu-add-category" onClick={addCat} className="btn-outline whitespace-nowrap">Add</button>
      </div>
      <div className="flex flex-wrap gap-2">
        {cats.map((c) => (
          <span key={c.id} className="badge-pine inline-flex items-center gap-2" data-testid={`menu-category-${c.id}`}>
            {c.name}{!c.active && " (hidden)"}
            <button data-testid={`menu-cat-rename-${c.id}`} onClick={() => renameCat(c)} className="underline text-xs">rename</button>
            <button data-testid={`menu-cat-toggle-${c.id}`} onClick={() => toggleCat(c)} className="underline text-xs">{c.active ? "disable" : "enable"}</button>
          </span>
        ))}
      </div>
    </div>
  );
}

function CategoryItems({ cat: c, onSave, onDelete }) {
  return (
    <div className="card" data-testid={`menu-cat-block-${c.id}`}>
      <h3 className="font-serif text-2xl mb-3">{c.name}{!c.active && <span className="badge-terracotta ml-2">hidden</span>}</h3>
      {(c.items || []).length === 0 && <p className="text-sm" style={{ color: "var(--muted)" }}>No items yet.</p>}
      <div className="space-y-2">
        {(c.items || []).map((it) => (
          <div key={it.id} className="flex flex-wrap items-center gap-2 py-2 border-b last:border-0" style={{ borderColor: "var(--border)" }} data-testid={`menu-item-${it.id}`}>
            <span className="font-medium flex-1 min-w-32">{it.name}{!it.available && <span className="badge-terracotta ml-2">unavailable</span>}</span>
            <input data-testid={`menu-item-order-${it.id}`} type="number" defaultValue={it.sort_order ?? 999} key={`o-${it.id}-${it.sort_order}`} title="Display order"
              onBlur={(e) => { const v = parseInt(e.target.value); if (!isNaN(v) && v !== (it.sort_order ?? 999)) onSave(it, { sort_order: v }); }}
              style={{ width: 64, padding: "6px 8px" }} />
            <input data-testid={`menu-item-price-${it.id}`} type="number" min="0" defaultValue={it.price} key={`p-${it.id}-${it.price}`}
              onBlur={(e) => { const v = parseFloat(e.target.value); if (!isNaN(v) && v !== it.price) onSave(it, { price: v }); }}
              style={{ width: 90, padding: "6px 10px" }} />
            <button data-testid={`menu-item-toggle-${it.id}`} onClick={() => onSave(it, { available: !it.available })} className="btn-outline" style={{ padding: "6px 12px", fontSize: 13 }}>
              {it.available ? "Mark unavailable" : "Mark available"}
            </button>
            <button data-testid={`menu-item-delete-${it.id}`} onClick={() => onDelete(it.id)} className="p-2" style={{ color: "var(--terracotta)" }}><Trash2 size={16} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}
